/**
 * MobileBottomNav - Bottom navigation bar for mobile e-commerce
 * 5 tabs: Boutique, Categories, Cart, Orders, Account
 */

import { useNavigate, useLocation } from 'react-router-dom';
import { Store, Grid3X3, ShoppingCart, Package, User } from 'lucide-react';
import { useNewPublicationsCount } from '../../../hooks/useNewPublicationsCount';
import { useEcommerceCart } from '../../../hooks/ecommerce/useEcommerceCart';
import { useCustomer } from '../../../contexts/CustomerContext';

interface NavItem {
  label: string;
  icon: typeof Store;
  path: string;
  badge?: number;
  authRequired?: boolean;
}

// Accent terracotta (design « marché artisanal »)
const ACCENT = '#b45f2e';

/**
 * ⚠️ Les libellés de cette barre sont la NAVIGATION du site sur téléphone.
 * Mesuré le 15/09/2026 : `text-gray-400` sur blanc donne 2,6:1 — très loin des
 * 4,5:1 exigés — et 10 px. Autrement dit, les quatre repères principaux du site
 * étaient les textes les moins lisibles de la page. Passés en `gray-600`
 * (5,7:1) et 11 px.
 */
export default function MobileBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { getItemCount } = useEcommerceCart();
  const { isAuthenticated } = useCustomer();

  const itemCount = getItemCount();
  const actusNonLues = useNewPublicationsCount();

  const navItems: NavItem[] = [
    { label: 'Boutique', icon: Store, path: '/shop' },
    { label: 'Actus', icon: Grid3X3, path: '/shop/feed', badge: actusNonLues || undefined },
    { label: 'Panier', icon: ShoppingCart, path: '/shop/cart', badge: itemCount || undefined },
    { label: 'Commandes', icon: Package, path: '/shop/orders', authRequired: true },
    { label: 'Compte', icon: User, path: isAuthenticated ? '/shop/profile' : '/shop/login' },
  ];

  const isActive = (path: string) => {
    if (path === '/shop') return location.pathname === '/shop';
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-[0_-2px_10px_rgba(0,0,0,0.08)] safe-area-bottom">
      <div className="flex items-center justify-around h-16 px-1">
        {navItems.map((item) => {
          const active = isActive(item.path);
          const Icon = item.icon;

          if (item.authRequired && !isAuthenticated) {
            return (
              <button
                key={item.label}
                onClick={() => navigate('/shop/login')}
                className="flex flex-col items-center justify-center flex-1 h-full relative transition-colors text-gray-600"
              >
                <Icon className="w-5 h-5" />
                <span className="text-[11px] mt-0.5 font-medium">{item.label}</span>
              </button>
            );
          }

          return (
            <button
              key={item.label}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center justify-center flex-1 h-full relative transition-colors ${
                active ? 'font-semibold' : 'text-gray-600'
              }`}
              style={active ? { color: ACCENT } : undefined}
            >
              <div className="relative">
                <Icon className="w-5 h-5" />
                {item.badge && item.badge > 0 && (
                  <span
                    className="absolute -top-2 -right-3 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-white text-[10px] font-bold px-1"
                    style={{ backgroundColor: ACCENT }}
                  >
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </div>
              <span className="text-[10px] mt-0.5">{item.label}</span>
              {active && (
                <div
                  className="absolute top-0 left-1/4 right-1/4 h-0.5 rounded-full"
                  style={{ backgroundColor: ACCENT }}
                />
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
